// features/findings/charts/stage-timeline.js
// Horizontal stage-progression timeline used by tectonic shift detail
// pages. Renders stageHistory as a horizontal arc with stage markers,
// date labels, and stage-name chips. The active stage is highlighted.
//
// Schema:
//   stageHistory: [{ date, stage, note }]
//   stages: ['early', 'accelerating', 'peak', 'declining', 'resolved'?]
//   currentStageIndex: number
//
// Render condition: requires ≥ 2 stage-history entries.

export function StageTimeline({ title, stageHistory, stages, currentStageIndex }) {
  if (!stageHistory || stageHistory.length < 2) return null;

  const W = 720;
  const H = 220;
  const ML = 60;
  const MR = 60;
  const MT = 80;
  const MB = 60;
  const innerW = W - ML - MR;
  const trackY = MT + 30;

  const n = stageHistory.length;
  const xAt = (i) => ML + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);

  return (
    <figure className="finding-chart">
      {title && <figcaption className="finding-chart-caption">{title}</figcaption>}
      <div className="finding-svg-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="finding-svg" role="img" aria-label={title || 'Stage timeline'}>
          {/* Track line */}
          <line
            x1={ML} y1={trackY}
            x2={W - MR} y2={trackY}
            className="stage-timeline-track"
          />

          {/* Stage segments — color-coded by stage progression */}
          {stages.map((stage, idx) => {
            const segmentStart = ML + (idx / stages.length) * innerW;
            const segmentEnd = ML + ((idx + 1) / stages.length) * innerW;
            const isActive = idx === currentStageIndex;
            const isPassed = idx < currentStageIndex;
            return (
              <g key={`segment-${stage}`}>
                <line
                  x1={segmentStart} y1={trackY}
                  x2={segmentEnd} y2={trackY}
                  className={
                    isActive ? 'stage-timeline-segment-active' :
                    isPassed ? 'stage-timeline-segment-passed' :
                    'stage-timeline-segment-future'
                  }
                />
                <text
                  x={(segmentStart + segmentEnd) / 2}
                  y={trackY + 22}
                  className={
                    isActive ? 'stage-timeline-stage-label-active' :
                    'stage-timeline-stage-label'
                  }
                  textAnchor="middle"
                  dominantBaseline="hanging"
                >
                  {stage}
                </text>
              </g>
            );
          })}

          {/* Stage history dots + date labels */}
          {stageHistory.map((h, i) => {
            const x = xAt(i);
            // Position alternates above/below track to avoid label collision
            const above = i % 2 === 0;
            const dotY = trackY;
            const dateY = above ? trackY - 38 : trackY + 56;
            const noteY = above ? trackY - 22 : trackY + 72;
            return (
              <g key={`hist-${i}`}>
                <circle
                  cx={x} cy={dotY} r={6}
                  className="stage-timeline-dot"
                />
                <text
                  x={x} y={dateY}
                  className="stage-timeline-date"
                  textAnchor="middle"
                >
                  {h.date}
                </text>
                <text
                  x={x} y={noteY}
                  className="stage-timeline-stage-tag"
                  textAnchor="middle"
                >
                  {h.stage}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </figure>
  );
}

export default StageTimeline;
